import Application from "../../Application";
import GetGenericCodebookInteractor from "../../codebooks/interactor/GetGenericCodebookInteractor";
import { ContentTypes } from "../common/Constants";
import CaseLawDocumentUpdatingPresenter, {
  ICaseLawDocumentUpdatingPresenter
} from "../presenter/CaseLawDocumentUpdatingPresenter";
import { TCaseLawDocumentPresentationModel } from "../presenter/model/CaseLawDocumentPresentationModel";
import GetAllCaseLawDocumentAttachmentsInteractor from "./attachments/GetAllCaseLawDocumentAttachmentsInteractor";
import FindCaseLawDocumentInteractor from "./FindCaseLawDocumentInteractor";
import GetAllOrganisationsInteractor from "../../organisations/interactors/GetAllOrganisationsInteractor";
import GetCaseLawDocumentIndexesInteractor from "./GetCaseLawDocumentIndexesInteractor";
import GetAllOrganisationAttachmentTypesInteractor from "../../organisations/interactors/GetAllOrganisationAttachmentTypesInteractor";
import GetCaseLawDocumentTypesForOrganisationInteractor from "../../organisations/interactors/GetCaseLawDocumentTypesForOrganisationInteractor";
import FindIndexesInCategoryForOrganisationInteractor from "../../organisations/interactors/FindIndexesInCategoryForOrganisationInteractor";
import GetInvalidatedCaseLawDocumentsInteractor from "./GetInvalidatedCaseLawDocumentsInteractor";
import GetAllCourtCaseConnectionTypesInteractor from "./GetAllCourtCaseConnectionTypesInteractor";
import { GenericCodebookTypes } from "../../core/common/Constants";

export default class ShowCaseLawDocumentUpdatingInteractor {
  private application: Application;
  private output?: ICaseLawDocumentUpdatingPresenter;
  private getAllOrganisationsInteractor: GetAllOrganisationsInteractor;
  private getCaseLawDocumentTypesForOrganisationInteractor: GetCaseLawDocumentTypesForOrganisationInteractor;
  private getAllOrganisationAttachmentTypesInteractor: GetAllOrganisationAttachmentTypesInteractor;
  private getGenericCodebookInteractor: GetGenericCodebookInteractor;
  private findCaseLawDocumentInteractor: FindCaseLawDocumentInteractor;
  private getCaseLawDocumentIndexesInteractor: GetCaseLawDocumentIndexesInteractor;
  private getAllCaseLawDocumentAttachmentsInteractor: GetAllCaseLawDocumentAttachmentsInteractor;
  private findIndexesInCategoryForOrganisationInteractor: FindIndexesInCategoryForOrganisationInteractor;
  private getInvalidatedCaseLawDocumentsInteractor: GetInvalidatedCaseLawDocumentsInteractor;
  private getAllCourtCaseConnectionTypesInteractor: GetAllCourtCaseConnectionTypesInteractor;

  constructor({
    application,
    getAllOrganisations,
    getCaseLawDocumentTypesForOrganisation,
    getAllOrganisationAttachmentTypes,
    getGenericCodebook,
    getAllCaseLawDocumentAttachments,
    findCaseLawDocument,
    getCaseLawDocumentIndexes,
    findIndexesInCategoryForOrganisation,
    getInvalidatedCaseLawDocuments,
    getAllCourtCaseConnectionTypes
  }) {
    this.application = application;
    this.getAllOrganisationsInteractor = getAllOrganisations;
    this.getCaseLawDocumentTypesForOrganisationInteractor =
      getCaseLawDocumentTypesForOrganisation;
    this.getGenericCodebookInteractor = getGenericCodebook;
    this.getAllOrganisationAttachmentTypesInteractor =
      getAllOrganisationAttachmentTypes;
    this.getGenericCodebookInteractor = getGenericCodebook;
    this.getAllCaseLawDocumentAttachmentsInteractor =
      getAllCaseLawDocumentAttachments;
    this.findCaseLawDocumentInteractor = findCaseLawDocument;
    this.getCaseLawDocumentIndexesInteractor = getCaseLawDocumentIndexes;
    this.findCaseLawDocumentInteractor = findCaseLawDocument;
    this.findIndexesInCategoryForOrganisationInteractor =
      findIndexesInCategoryForOrganisation;
    this.getInvalidatedCaseLawDocumentsInteractor =
      getInvalidatedCaseLawDocuments;
    this.getAllCourtCaseConnectionTypesInteractor =
      getAllCourtCaseConnectionTypes;
  }

  private async getAllOrganisations() {
    return this.getAllOrganisationsInteractor.execute();
  }
  private async getCaseLawDocumentTypesForOrganisation(organisationId) {
    return this.getCaseLawDocumentTypesForOrganisationInteractor.execute(
      organisationId
    );
  }
  private async getInvalidatedCaseLawDocuments(id) {
    return this.getInvalidatedCaseLawDocumentsInteractor.execute(id);
  }
  private async getAllCourtCaseConnectionTypes() {
    return this.getAllCourtCaseConnectionTypesInteractor.execute();
  }
  private async getAllAttachmentTypes(organisationId) {
    return this.getAllOrganisationAttachmentTypesInteractor.execute(
      organisationId
    );
  }
  private async getContentTypes() {
    return this.getGenericCodebookInteractor.execute(
      ContentTypes.ATTACHMENT_CONTENT_TYPE,
      false
    );
  }
  private async getCourtCaseTypes() {
    return this.getGenericCodebookInteractor.execute(
      GenericCodebookTypes.COURT_CASE_TYPE,
      false
    );
  }
  private async getDecisionTypes() {
    return this.getGenericCodebookInteractor.execute(
      GenericCodebookTypes.DECISION_TYPE,
      false
    );
  }

  private async getReferenceLists(
    organisationId: number,
    caseLawDocumentId: number
  ) {
    return Promise.all([
      this.getAllOrganisations(),
      this.getAllAttachmentTypes(organisationId),
      this.getCaseLawDocumentTypesForOrganisation(organisationId),
      this.getContentTypes(),
      this.getInvalidatedCaseLawDocuments(caseLawDocumentId),
      this.getAllCourtCaseConnectionTypes(),
      this.getCourtCaseTypes(),
      this.getDecisionTypes()
    ]);
  }

  execute(id: number) {
    this.output = CaseLawDocumentUpdatingPresenter({
      application: this.application,

      initialState: {
        previousSelectedIds: [],
        caseLawDocument: {} as TCaseLawDocumentPresentationModel,
        organisations: [],
        caseLawDocumentTypes: [],
        caseLawDocumentIndexes: [],
        attachmentTypes: [],
        attachmentContentTypes: [],
        caseLawDocumentAttachments: [],
        uploadedFile: undefined,
        indexHierarchyList: [],
        connectedCourtCasesList: [],
        contentTypes: [],
        selectedOrganisationId: NaN,
        organisationIndexes: [],
        selectedCourtCases: [],
        selectedIndex: [],
        organisationCaseLawDocumentTypes: [],
        nonAnonymisedAttachmentsCount: NaN,
        parentGroupList: [],
        secondGroupList: [],
        selectedParentGroupIdList: [],
        selectedSecondGroupIdList: [],
        invalidatedCaseLawDocuments: [],
        publishedCaseLawDocuments: [],
        invalidatePublishedCaseLawDocsVisible: false,
        courtCaseConnectionTypeList: [],
        createCourtCasePopupVisible: false,
        createCourtCaseModalVisible: false,
        anonimisedUploadedFile: undefined,
        courtCaseTypes: [],
        decisionTypes: [],
        indexDataModalVisible: false
      }
    });
    this.findCaseLawDocumentInteractor.execute(id).then((caseLawDocument) => {
      this.getReferenceLists(caseLawDocument.organisationId, id)
        .then(
          ([
            organisations,
            attachmentTypes,
            organisationCaseLawDocumentTypes,
            contentTypes,
            invalidatedCaseLawDocuments,
            courtCaseConnectionTypeList,
            courtCaseTypes,
            decisionTypes
          ]) =>
            this.output?.loadReferenceValues(
              organisations,
              attachmentTypes,
              organisationCaseLawDocumentTypes,
              contentTypes,
              invalidatedCaseLawDocuments,
              courtCaseConnectionTypeList,
              courtCaseTypes,
              decisionTypes
            )
        )
        .then(() => this.output?.loadCaseLawDocument(caseLawDocument))
        .then(() => this.getAllCaseLawDocumentAttachmentsInteractor.execute(id))
        .then(this.output?.loadAttachments)
        .then(() => this.getCaseLawDocumentIndexesInteractor.execute(id))
        .then(this.output?.loadCaseLawDocumentIndexes)
        .then(() =>
          this.findIndexesInCategoryForOrganisationInteractor.execute(
            caseLawDocument.documentTypeId,
            caseLawDocument.organisationId
          )
        )
        .then(this.output?.loadCaseLawDocumentTypeIndexes)
        .then(() => this.output?.loadCaseLawDocument(caseLawDocument));
    });

    return this.output;
  }
}
